import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #000;
  margin: 0;
  font-weight: 600;
`;

const BackButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const ContinueButton = styled(BackButton)`
  background: #000;
  margin-right: 1rem;

  &:hover {
    background: #333;
  }
`;

const PaymentCompleteButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusSection = styled.div`
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const DetailSection = styled.div`
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 2px solid #000;
  font-weight: 600;
  color: #000;
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  align-items: start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const DataLabel = styled.div`
  font-weight: 500;
  color: #000;
`;

const DataValue = styled.div`
  color: #000;
`;

const FileLink = styled.a`
  color: #000;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OwnerCard = styled.div`
  border: 2px solid #000;
  border-radius: 15px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #f8f9fa;
`;

const OwnerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
`;

const OwnerName = styled.div`
  font-weight: 600;
  color: #000;
  font-size: 1.1rem;
`;

const OwnerShare = styled.div`
  background: #000;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const OwnerDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OwnerDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  color: #000;
  font-weight: 500;
`;

const DetailValue = styled.div`
  color: #000;
`;

// Document Management Styled Components
const DocumentList = styled.div`
  margin-top: 1.5rem;
`;

const DocumentItem = styled.div`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const DocumentIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #000;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentName = styled.h3`
  font-weight: 600;
  color: #000;
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  line-height: 1.4;
`;

const DocumentMeta = styled.div`
  font-size: 0.9rem;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: #cbd5e0;
    border-radius: 50%;
  }
  
  &:first-child::before {
    display: none;
  }
`;

const DocumentActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 25px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &:not(:disabled) {
    background: #000;
    color: white;

    &:hover {
      background: #333;
      transform: translateY(-1px);
    }
  }

  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
  }
`;

const UploadSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border: 2px dashed #000;
  border-radius: 15px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #000;
    background: #f1f5f9;
  }
`;

const UploadArea = styled.div`
  flex: 1;
  text-align: left;
`;

const UploadTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #000;
  margin-bottom: 0.75rem;
`;

const UploadControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const UploadButtonContainer = styled.div`
  flex-shrink: 0;
`;

const FileInputWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: white;
  border: 2px solid #000;
  border-radius: 25px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #000;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
`;

const FileName = styled.div`
  font-size: 0.8rem;
  color: #718096;
  margin-top: 0.5rem;
  min-height: 1.2rem;
`;

const UploadButton = styled.button`
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #28a745;
  color: white;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusBadge = styled.span<{ status: string; type?: 'registration' | 'document' }>`
  padding: ${props => props.type === 'registration' ? '0.5rem 1rem' : '0.25rem 0.5rem'};
  border-radius: ${props => props.type === 'registration' ? '20px' : '12px'};
  font-size: ${props => props.type === 'registration' ? '0.9rem' : '0.8rem'};
  font-weight: 500;
  margin-left: ${props => props.type === 'document' ? '0.5rem' : '0'};
  
  ${props => {
    if (props.type === 'registration') {
      switch (props.status) {
        case 'draft':
          return 'background: #e9ecef; color: #495057;';
        case 'submitted':
          return 'background: #d1ecf1; color: #0c5460;';
        case 'awaiting_payment':
          return 'background: #fff3cd; color: #856404;';
        case 'payment_verification':
          return 'background: #f8d7da; color: #721c24;';
        case 'in_progress':
          return 'background: #cce5ff; color: #004085;';
        case 'awaiting_documents':
          return 'background: #e1f5fe; color: #01579b;';
        case 'awaiting_signatures':
          return 'background: #fff8e1; color: #e65100;';
        case 'completed':
          return 'background: #d4edda; color: #155724;';
        case 'rejected':
          return 'background: #f8d7da; color: #721c24;';
        default:
          return 'background: #e9ecef; color: #495057;';
      }
    } else {
      // Document status
      switch (props.status) {
        case 'uploaded_by_admin':
          return 'background: #e3f2fd; color: #1976d2;';
        case 'uploaded_by_user':
          return 'background: #fff3e0; color: #f57c00;';
        case 'completed':
          return 'background: #e8f5e8; color: #2e7d32;';
        default:
          return 'background: #f5f5f5; color: #666;';
      }
    }
  }}
`;


const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #000;
  background: #f8f9fa;
  border-radius: 15px;
  border: 2px solid #000;
`;

const NotificationBanner = styled.div<{ type: 'info' | 'success' | 'warning' }>`
  padding: 1rem;
  border-radius: 15px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 2px solid #000;
  
  ${props => {
    switch (props.type) {
      case 'info':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'success':
        return 'background: #d4edda; color: #155724;';
      case 'warning':
        return 'background: #fff3cd; color: #856404;';
      default:
        return 'background: #f8f9fa; color: #000;';
    }
  }}
`;

const DocumentProgress = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  border: 2px solid #000;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ProgressStep = styled.div<{ completed: boolean; current: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  color: ${props => props.completed ? '#28a745' : props.current ? '#000' : '#000'};
  font-weight: ${props => props.current ? '600' : '500'};
  
  &::before {
    content: ${props => props.completed ? '"✓"' : props.current ? '"→"' : '"○"'};
    font-weight: bold;
    width: 20px;
    text-align: center;
  }
`;

interface RegistrationDetail {
  id: number;
  status: string;
  company_type?: string;
  country?: string;
  business_activity_code: string;
  business_activity: string;
  business_description: string;
  company_name_armenian: string;
  company_name_english: string;
  company_name_russian: string;
  owners_data: string;
  authorized_capital: number;
  decision_making: string;
  charter_preferences: string;
  additional_services: string;
  additional_comments: string;
  preferred_communication: string;
  payment_link?: string;
  created_at: string;
  updated_at: string;
}

interface Owner {
  full_name: string;
  address: string;
  passport_scan_path?: string;
  additional_proof_path?: string;
  share_percentage: number;
}

const MyRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [registration, setRegistration] = useState<RegistrationDetail | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingPaymentComplete, setIsMarkingPaymentComplete] = useState(false);
  
  // Document management state
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState<{[key: number]: boolean}>({});
  const [selectedFiles, setSelectedFiles] = useState<{[key: number]: File | null}>({});

  // Function to handle secure file downloads
  const handleFileDownload = async (filename: string, documentType: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required to download files');
        return;
      }

      const response = await axios.get(
        getCurrentApiEndpoints().DOWNLOAD_FILE(filename),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleMarkPaymentComplete = async () => {
    if (!registration) return;
    
    const confirmed = window.confirm(
      'Are you sure you have completed the payment? This will mark your registration for verification.'
    );
    
    if (!confirmed) return;
    
    setIsMarkingPaymentComplete(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        getCurrentApiEndpoints().MARK_PAYMENT_COMPLETE(registration.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRegistration(prev => prev ? { ...prev, status: 'payment_verification' } : null);
      alert('Payment marked as completed! Your registration is now under verification.');
    } catch (error) {
      console.error('Failed to mark payment complete:', error);
      alert('Failed to mark payment as complete. Please try again.');
    } finally {
      setIsMarkingPaymentComplete(false);
    }
  };

  // Document management functions
  const loadDocuments = async () => {
    if (!registration) return;
    
    setIsLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        getCurrentApiEndpoints().USER_GET_DOCUMENTS(registration.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDocumentDownload = async (documentId: number) => {
    if (!registration) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        getCurrentApiEndpoints().USER_DOWNLOAD_DOCUMENT(registration.id, documentId),
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' 
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `document_${documentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document');
    }
  };

  const handleFileSelect = (documentId: number, file: File | null) => {
    setSelectedFiles(prev => ({ ...prev, [documentId]: file }));
  };

  const handleSignedDocumentUpload = async (documentId: number) => {
    if (!registration || !selectedFiles[documentId]) return;

    setUploadingDocuments(prev => ({ ...prev, [documentId]: true }));
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFiles[documentId]!);

      await axios.post(
        getCurrentApiEndpoints().USER_UPLOAD_SIGNED_DOCUMENT(registration.id, documentId),
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Clear selected file and reload documents
      setSelectedFiles(prev => ({ ...prev, [documentId]: null }));
      await loadDocuments();
      await fetchRegistration();
      
      alert('Signed document uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload signed document:', error);
      alert('Failed to upload signed document');
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [documentId]: false }));
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'uploaded_by_admin':
        return 'Ready for Signature';
      case 'uploaded_by_user':
        return 'Signed & Uploaded';
      case 'completed':
        return 'Approved';
      default:
        return 'Pending';
    }
  };

    const fetchRegistration = async () => {
      try {
      const response = await axios.get(getCurrentApiEndpoints().MY_REGISTRATION_BY_ID(parseInt(id!)));
        setRegistration(response.data);
      } catch (error) {
        console.error('Failed to fetch registration:', error);
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    if (id) {
      fetchRegistration();
    }
  }, [id]);

  // Load documents when registration is available
  useEffect(() => {
    if (registration) {
      loadDocuments();
    }
  }, [registration]);

  const parseOwners = (ownersData: string): Owner[] => {
    try {
      return ownersData ? JSON.parse(ownersData) : [];
    } catch {
      return [];
    }
  };

  const parseJson = (jsonString: string | null | undefined) => {
    if (!jsonString) return {};
    try {
      return JSON.parse(jsonString);
    } catch {
      return {};
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          Loading registration details...
        </div>
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          Registration not found
        </div>
      </Container>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Under Review';
      case 'awaiting_payment': return 'Awaiting Payment';
      case 'payment_verification': return 'Payment Verification';
      case 'in_progress': return 'In Progress';
      case 'awaiting_documents': return 'Awaiting Documents';
      case 'awaiting_signatures': return 'Awaiting Signatures';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const owners = parseOwners(registration.owners_data || '');
  const services = parseJson(registration.additional_services);
  
  // Helper function to parse and format charter preferences
  const parseCharterPreferences = (preferencesJson: string) => {
    try {
      const prefs = JSON.parse(preferencesJson);
      const preferenceLabels: Record<string, string> = {
        allow_electronic_meetings: 'Allow Electronic Meetings',
        require_board_approval: 'Require Board Approval for Major Transactions',
        preemptive_rights: 'Preemptive Rights',
        drag_along_rights: 'Drag-Along Rights',
        tag_along_rights: 'Tag-Along Rights'
      };
      
      return Object.entries(prefs)
        .filter(([_, value]) => value === true)
        .map(([key, _]) => preferenceLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    } catch {
      return [];
    }
  };
  
  const charterPreferences = parseCharterPreferences(registration.charter_preferences || '{}');

  const selectedServices = Object.entries(services)
    .filter(([_, selected]) => selected)
    .map(([key, _]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

  return (
    <Container>
      <Header>
        <Title>Registration #{registration.id}</Title>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {registration.status === 'draft' && (
            <ContinueButton onClick={() => navigate(`/registration/${registration.id}`)}>
              Continue Editing
            </ContinueButton>
          )}
          <BackButton onClick={() => navigate('/my-requests')}>
            ← Back to My Requests
          </BackButton>
        </div>
      </Header>

      <StatusSection>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <StatusBadge status={registration.status} type="registration">
            {getStatusLabel(registration.status)}
          </StatusBadge>
        </div>
      </StatusSection>

      {registration.status === 'awaiting_payment' && registration.payment_link && (
        <StatusSection style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 1rem 0', color: 'white' }}>💳 Payment Required</h2>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem' }}>
              Your registration is ready for payment. Please complete the payment to proceed.
            </p>
            <a
              href={registration.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: 'white',
                color: '#667eea',
                padding: '1rem 2rem',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s ease',
                marginRight: '1rem'
              }}
              onMouseOver={(e) => (e.target as HTMLAnchorElement).style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => (e.target as HTMLAnchorElement).style.transform = 'translateY(0)'}
            >
              🚀 Proceed to Payment
            </a>
            <PaymentCompleteButton
              onClick={handleMarkPaymentComplete}
              disabled={isMarkingPaymentComplete}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid white',
                color: 'white',
                marginTop: '0'
              }}
            >
              {isMarkingPaymentComplete ? '⏳ Processing...' : '✅ Mark Payment Complete'}
            </PaymentCompleteButton>
          </div>
        </StatusSection>
      )}

      {registration.status === 'payment_verification' && (
        <StatusSection style={{ background: 'linear-gradient(135deg, #ffc107 0%, #ff8c00 100%)', color: 'white' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 1rem 0', color: 'white' }}>⏳ Payment Verification</h2>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem' }}>
              Your payment has been received and is being verified by our team. 
              We will update your registration status once verification is complete.
            </p>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.2)', 
              padding: '1rem', 
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}>
              <strong>What happens next?</strong><br/>
              • Our team will verify your payment<br/>
              • You'll receive an email confirmation<br/>
              • Your registration will move to "In Progress" status
          </div>
          </div>
        </StatusSection>
      )}

      {registration.status === 'in_progress' && (
        <StatusSection style={{ background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 1rem 0', color: 'white' }}>🚀 Registration In Progress</h2>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.1rem' }}>
              Your payment has been verified and your company registration is now being processed. 
              We're working on completing your registration.
            </p>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.2)', 
              padding: '1rem', 
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}>
              <strong>Current Status:</strong><br/>
              • Payment verified ✅<br/>
              • Registration documents being prepared<br/>
              • Final review in progress<br/>
              • You'll be notified when completed
            </div>
          </div>
      </StatusSection>
      )}

      <DetailSection>
        <SectionHeader>Business Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Company Type:</DataLabel>
            <DataValue>{registration.company_type || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Country:</DataLabel>
            <DataValue>{registration.country || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Activity Code:</DataLabel>
            <DataValue>{registration.business_activity_code || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Activity:</DataLabel>
            <DataValue>{registration.business_activity || 'Not specified'}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Description:</DataLabel>
            <DataValue>{registration.business_description || 'Not specified'}</DataValue>
          </DataGrid>
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Company Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Armenian Name:</DataLabel>
            <DataValue>{registration.company_name_armenian || 'Not specified'}</DataValue>
          </DataGrid>
          {registration.company_name_english && (
            <DataGrid>
              <DataLabel>English Name:</DataLabel>
              <DataValue>{registration.company_name_english}</DataValue>
            </DataGrid>
          )}
          {registration.company_name_russian && (
            <DataGrid>
              <DataLabel>Russian Name:</DataLabel>
              <DataValue>{registration.company_name_russian}</DataValue>
            </DataGrid>
          )}
        </SectionContent>
      </DetailSection>

      <DetailSection>
        <SectionHeader>Owners Information & Share Distribution</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Authorized Capital:</DataLabel>
            <DataValue>{registration.authorized_capital ? `${registration.authorized_capital.toLocaleString()} AMD` : 'Not specified'}</DataValue>
          </DataGrid>
          
          <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
            <DataLabel style={{ marginBottom: '1rem' }}>Owners:</DataLabel>
            {owners.length > 0 ? (
              owners.map((owner, index) => (
                <OwnerCard key={index}>
                  <OwnerHeader>
                    <OwnerName>Owner {index + 1}: {owner.full_name}</OwnerName>
                    <OwnerShare>{owner.share_percentage}% Share</OwnerShare>
                  </OwnerHeader>
                  <OwnerDetails>
                    <OwnerDetail>
                      <DetailLabel>Address:</DetailLabel>
                      <DetailValue>{owner.address}</DetailValue>
                    </OwnerDetail>
                    <OwnerDetail>
                      <DetailLabel>Documents:</DetailLabel>
                      <DetailValue>
                        {owner.passport_scan_path ? (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <FileLink 
                              onClick={() => handleFileDownload(owner.passport_scan_path!, 'passport')}
                              style={{ cursor: 'pointer' }}
                            >
                              📄 View Passport Scan
                            </FileLink>
                          </div>
                        ) : (
                          <div style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                            ❌ Passport scan not uploaded
                          </div>
                        )}
                        {owner.additional_proof_path ? (
                          <div style={{ marginTop: '0.5rem' }}>
                            <FileLink 
                              onClick={() => handleFileDownload(owner.additional_proof_path!, 'additional_proof')}
                              style={{ cursor: 'pointer' }}
                            >
                              📄 View Additional Proof
                            </FileLink>
                          </div>
                        ) : (
                          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                            No additional proof uploaded
                          </div>
                        )}
                      </DetailValue>
                    </OwnerDetail>
                  </OwnerDetails>
                </OwnerCard>
              ))
            ) : (
              <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No owners information available
              </div>
            )}
          </div>
        </SectionContent>
      </DetailSection>

        <DetailSection>
        <SectionHeader>Charter Details</SectionHeader>
          <SectionContent>
            <DataGrid>
            <DataLabel>Decision Making:</DataLabel>
            <DataValue>{registration.decision_making?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
            </DataGrid>
          {charterPreferences.length > 0 && (
            <DataGrid>
              <DataLabel>Additional Charter Preferences:</DataLabel>
              <DataValue>
                {charterPreferences.map((preference, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#28a745', marginRight: '0.5rem' }}>✓</span>
                    {preference}
                  </div>
                ))}
              </DataValue>
            </DataGrid>
          )}
          </SectionContent>
        </DetailSection>

      <DetailSection>
        <SectionHeader>Additional Information</SectionHeader>
        <SectionContent>
          <DataGrid>
            <DataLabel>Communication Method:</DataLabel>
            <DataValue>{registration.preferred_communication?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</DataValue>
          </DataGrid>
          {selectedServices.length > 0 && (
          <DataGrid>
              <DataLabel>Selected Services:</DataLabel>
            <DataValue>
                {selectedServices.map((service, index) => (
                  <div key={index}>• {service}</div>
                ))}
            </DataValue>
          </DataGrid>
          )}
          {registration.additional_comments && (
            <DataGrid>
              <DataLabel>Comments:</DataLabel>
              <DataValue>{registration.additional_comments}</DataValue>
            </DataGrid>
          )}
          <DataGrid>
            <DataLabel>Created:</DataLabel>
            <DataValue>{formatDate(registration.created_at)}</DataValue>
          </DataGrid>
          <DataGrid>
            <DataLabel>Last Updated:</DataLabel>
            <DataValue>{formatDate(registration.updated_at)}</DataValue>
          </DataGrid>
        </SectionContent>
      </DetailSection>

      {/* Document Management Section - Only show for certain statuses */}
      {(registration.status === 'awaiting_documents' || registration.status === 'awaiting_signatures' || documents.length > 0) && (
        <DetailSection>
          <SectionHeader>Document Management</SectionHeader>
          <SectionContent>
            {/* Status-specific notification banner */}
            {registration.status === 'awaiting_documents' && (
              <NotificationBanner type="info">
                <span>📄</span>
                <div>
                  <strong>Documents Ready for Signature</strong><br/>
                  Download documents prepared by our team, sign them, and upload the signed versions.
                </div>
              </NotificationBanner>
            )}
            
            {registration.status === 'awaiting_signatures' && (
              <NotificationBanner type="warning">
                <span>⏳</span>
                <div>
                  <strong>Awaiting Your Signature</strong><br/>
                  Upload your signed documents to complete the registration process.
                </div>
              </NotificationBanner>
            )}
            
            {registration.status === 'completed' && documents.length > 0 && (
              <NotificationBanner type="success">
                <span>🎉</span>
                <div>
                  <strong>Registration Completed!</strong><br/>
                  All documents have been approved and your company registration is complete.
                </div>
              </NotificationBanner>
            )}

            {/* Document workflow progress */}
            <DocumentProgress>
              <h4 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem' }}>
                Document Workflow Progress
              </h4>
              <ProgressStep 
                completed={documents.length > 0} 
                current={registration.status === 'awaiting_documents'}
              >
                Documents prepared by team
              </ProgressStep>
              <ProgressStep 
                completed={documents.some(doc => doc.status === 'uploaded_by_user' || doc.status === 'completed')} 
                current={registration.status === 'awaiting_signatures'}
              >
                Documents signed and uploaded
              </ProgressStep>
              <ProgressStep 
                completed={registration.status === 'completed'} 
                current={false}
              >
                Documents approved and registration completed
              </ProgressStep>
            </DocumentProgress>
            
            <DocumentList>
              {isLoadingDocuments ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  <LoadingSpinner />
                  Loading documents...
                </div>
              ) : documents.length === 0 ? (
                <EmptyState>
                  No documents available yet. Documents will appear here once they are prepared by our team.
                </EmptyState>
              ) : (
                documents.map((doc) => (
                  <DocumentItem key={doc.id}>
                    <DocumentHeader>
                      <DocumentInfo>
                        <DocumentIcon>📄</DocumentIcon>
                        <DocumentDetails>
                          <DocumentName>{doc.document_name}</DocumentName>
                          <DocumentMeta>
                            <MetaItem>{doc.document_type}</MetaItem>
                            <MetaItem>{new Date(doc.created_at).toLocaleDateString()}</MetaItem>
                            <MetaItem>{doc.uploaded_by_admin ? 'Prepared by Team' : 'Uploaded by You'}</MetaItem>
                          </DocumentMeta>
                        </DocumentDetails>
                      </DocumentInfo>
                      <DocumentActions>
                        <StatusBadge status={doc.status} type="document">
                          {getStatusDisplayName(doc.status)}
                        </StatusBadge>
                        <ActionButton onClick={() => handleDocumentDownload(doc.id)}>
                          Download
                        </ActionButton>
                      </DocumentActions>
                    </DocumentHeader>
                    
                    {/* Show upload section for documents ready for signature */}
                    {doc.status === 'uploaded_by_admin' && (
                      <UploadSection>
                        <UploadArea>
                          <UploadTitle>Upload signed version</UploadTitle>
                          <UploadControls>
                            <FileInputWrapper>
                              <FileInputLabel htmlFor={`file-${doc.id}`}>
                                Choose File
                              </FileInputLabel>
                              <HiddenFileInput
                                id={`file-${doc.id}`}
                                type="file"
                                accept=".pdf"
                                onChange={(e) => handleFileSelect(doc.id, e.target.files?.[0] || null)}
                              />
                              <FileName>
                                {selectedFiles[doc.id] ? selectedFiles[doc.id]?.name : 'No file selected'}
                              </FileName>
                            </FileInputWrapper>
                          </UploadControls>
                        </UploadArea>
                        <UploadButtonContainer>
                          <UploadButton
                            onClick={() => handleSignedDocumentUpload(doc.id)}
                            disabled={!selectedFiles[doc.id] || uploadingDocuments[doc.id]}
                          >
                            {uploadingDocuments[doc.id] ? (
                              <>
                                <LoadingSpinner />
                                Uploading...
                              </>
                            ) : (
                              'Upload Signed'
                            )}
                          </UploadButton>
                        </UploadButtonContainer>
                      </UploadSection>
                    )}
                  </DocumentItem>
                ))
              )}
            </DocumentList>
          </SectionContent>
        </DetailSection>
      )}

    </Container>
  );
};

export default MyRequestDetail;